import patientTagsService from '@/services/patientTagsService';

/**
 * List/search the available tag catalog for the current clinic
 * @param {string} query - optional search query
 * @returns {Promise} API response with { tags: [...], statusCode, errorMessage }
 */
export const getTagCatalogApi = async (query = '') => {
  try {
    const params = query ? { query } : {};
    const response = await patientTagsService.get('/patienttags/tags', { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch tag catalog:', error);
    throw error;
  }
};

/**
 * Create a new tag in the global catalog
 * @param {string} tagName - tag name to create
 * @returns {Promise} API response with { tag: {...}, statusCode, errorMessage }
 */
export const createTagApi = async (tagName) => {
  try {
    const response = await patientTagsService.post('/patienttags/tags', {
      patient_flag_name: tagName
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create tag:', error);
    throw error;
  }
};

/**
 * Get patient's assigned tags (clinic scoped)
 * @param {string|number} patientId - patient ID
 * @returns {Promise} API response with { tags: [...], statusCode, errorMessage }
 */
export const getPatientTagsApi = async (patientId) => {
  try {
    if (!patientId || patientId === 'null' || patientId === 'undefined') {
      console.warn('Invalid patientId provided to getPatientTagsApi, skipping request');
      return { tags: [], statusCode: 400, errorMessage: 'Invalid patient ID' };
    }
    const response = await patientTagsService.get(`/patienttags/patients/${patientId}/tags`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch patient tags:', error);
    throw error;
  }
};

/**
 * Assign an existing tag to a patient by tag ID
 * @param {string|number} patientId - patient ID
 * @param {number} tagId - patient_flag_id to assign
 * @returns {Promise} API response with { statusCode, errorMessage }
 */
export const assignTagToPatientApi = async (patientId, tagId) => {
  try {
    const response = await patientTagsService.post(`/patienttags/patients/${patientId}/tags/assign`, {
      patient_flag_id: tagId
    });
    return response.data;
  } catch (error) {
    console.error('Failed to assign tag to patient:', error);
    throw error;
  }
};

/**
 * Create and assign a tag to a patient by name
 * @param {string|number} patientId - patient ID
 * @param {string} tagName - tag name to create and assign
 * @returns {Promise} API response with { tag: {...}, statusCode, errorMessage }
 */
export const createAndAssignTagApi = async (patientId, tagName) => {
  try {
    const response = await patientTagsService.post(`/patienttags/patients/${patientId}/tags`, {
      patient_flag_name: tagName
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create and assign tag:', error);
    throw error;
  }
};

/**
 * Remove a tag assignment from a patient
 * @param {string|number} patientId - patient ID
 * @param {number} tagId - patient_flag_id to remove
 * @returns {Promise} API response with { statusCode, errorMessage }
 */
/**
 * List patients under a specific tag (clinic scoped, paginated)
 * @param {number} tagId - tag ID
 * @param {Object} options - { limit, cursor }
 * @returns {Promise} API response with { tag, total, patients, nextCursor }
 */
export const getTagPatientsApi = async (tagId, { limit = 100, cursor } = {}) => {
  try {
    const params = { limit };
    if (cursor) params.cursor = cursor;
    const response = await patientTagsService.get(`/patienttags/tags/${tagId}/patients`, { params });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch tag patients:', error);
    throw error;
  }
};

export const removeTagFromPatientApi = async (patientId, tagId) => {
  try {
    const response = await patientTagsService.delete(`/patienttags/patients/${patientId}/tags/${tagId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to remove tag from patient:', error);
    throw error;
  }
};
